import type {
  ToastManagerAddOptions as BaseAddOptions,
  ToastManager as BaseToastManager,
  ToastManagerUpdateOptions as BaseUpdateOptions,
} from '@base-ui/react/toast'
import type { ToastData } from './types'

import { Toast as BaseToast } from '@base-ui/react/toast'

let lastToastId = 0

function nextToastId() {
  lastToastId += 1
  return `toast-${lastToastId}`
}

export interface BufferedToastManager {
  /** Passed to Base UI's provider, which subscribes to it. */
  base: BaseToastManager<ToastData>
  add(options: BaseAddOptions<ToastData>): string
  update(id: string, updates: BaseUpdateOptions<ToastData>): void
  close(id?: string): void
  /** Replays queued calls; later calls go straight through. */
  flush(): void
  /** Queues calls again until the next flush. */
  pause(): void
}

/**
 * Base UI subscribes to its manager in an effect, after children's effects
 * run, so calls are queued until the provider flushes.
 */
export function createBufferedManager(): BufferedToastManager {
  const base = BaseToast.createToastManager<ToastData>()
  let queue: Array<() => void> | null = []

  function run(call: () => void) {
    if (queue) {
      queue.push(call)
      return
    }
    call()
  }

  return {
    base,
    add(options) {
      // Generated here so an early update or close can target the queued toast.
      const id = options.id || nextToastId()
      run(() => base.add({ ...options, id }))
      return id
    },
    update(id, updates) {
      run(() => base.update(id, updates))
    },
    close(id) {
      run(() => base.close(id))
    },
    flush() {
      const pending = queue ?? []
      queue = null
      for (const call of pending) {
        call()
      }
    },
    pause() {
      queue ??= []
    },
  }
}
